// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// The integration registry — the Integrations sub-view of the unified
// Integrations tab (docs/28 §/integrations). One card per integration with its
// credentials table and grant ledger inside, plus the register / add-credential /
// edit / delete / Knowledge flows. Credential ledgers expose the same grant
// decisions as the cross-app Grants sub-view.
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { IntegrationAgentActions } from '@/components/agents/IntegrationAgentActions';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { GroupCard } from '@/components/greenlight/GroupCard';
import { LoadMore } from '@/components/greenlight/LoadMore';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import {
  presentIntegrationCard,
  presentSummary,
} from '@/components/greenlight/integrationPresenter';
import type { IntegrationCardVM } from '@/components/greenlight/integrationPresenter';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { AddCredentialDialog } from '@/components/integrations/AddCredentialDialog';
import { isPlainHttpUrl, NotEncryptedBadge } from '@/components/integrations/IntegrationFields';
import { TlsConfigBadge } from '@/components/integrations/CertificateTrustSection';
import { CredentialsTable } from '@/components/integrations/CredentialsTable';
import { useSignInTests, type SignInTests } from '@/components/integrations/SignInTest';
import type { CredentialTestResultBody } from '@/components/integrations/CredentialTestResult';
import { DeleteCredentialDialog } from '@/components/integrations/DeleteCredentialDialog';
import { DeleteIntegrationDialog } from '@/components/integrations/DeleteIntegrationDialog';
import { EditCredentialDialog } from '@/components/integrations/EditCredentialDialog';
import { IntegrationDialog } from '@/components/integrations/IntegrationDialog';
import { ConnectionRequestsQueue } from '@/components/integrations/ConnectionRequestsQueue';
import { IntegrationKnowledgeSheet } from '@/components/integrations/IntegrationKnowledgeSheet';
import {
  blockingGrantsFromError,
  blockingHolderNames,
  type BlockingGrants,
} from '@/components/integrations/blockingGrants';
import { api } from '@/lib/api';
import { isPreviewFeatureEnabled } from '@/lib/preview-features';
import { useCursorPages } from '@/lib/useCursorPages';
import { cn } from '@/lib/utils';
import { isPerUserOauthIntegration } from '@greenlight/shared';
import type { IntegrationListItem, PermissionListItem } from '@greenlight/shared';

// The registry is cursor-paginated (docs/16). This view accumulates pages: the
// summary counts, the env-var collision list, and the deep-link focus target all
// read the whole registry, not whichever slice fits one page.
const PAGE_SIZE = 50;

const integrationKey = (item: IntegrationListItem): string => item.id;

export function IntegrationsView({
  userRole,
  registerOpen,
  onRegisterOpenChange,
  pendingGrants,
  requireDataAccessTerms = false,
  onDataAccessTermsAccepted,
}: {
  userRole: string;
  // The "Register integration" trigger lives in the unified tab's page header,
  // so the page owns the open state and hands it down here.
  registerOpen: boolean;
  onRegisterOpenChange: (open: boolean) => void;
  pendingGrants?: PermissionListItem[];
  requireDataAccessTerms?: boolean;
  onDataAccessTermsAccepted?: () => void;
}): React.JSX.Element {
  const [addCredentialFor, setAddCredentialFor] = React.useState<IntegrationListItem | null>(null);
  const [editCredentialFor, setEditCredentialFor] = React.useState<{
    integration: IntegrationListItem;
    slug: string;
  } | null>(null);
  const [deleteCredentialFor, setDeleteCredentialFor] = React.useState<{
    integration: IntegrationListItem;
    slug: string;
  } | null>(null);
  const [editIntegrationFor, setEditIntegrationFor] = React.useState<IntegrationListItem | null>(
    null,
  );
  // Registering and editing share one IntegrationDialog instance (docs/28
  // §/integrations); activating either trigger must deactivate the other, or
  // the shared dialog can get stuck showing the wrong mode — e.g. the header's
  // "Register integration" appears to do nothing while an edit is open,
  // because `registerOpen` flips true but `editIntegrationFor` (which the
  // dialog's `open` prop also ORs against) is never cleared. `registerOpen` is
  // owned by the parent page, so this effect is the only way to react to it.
  React.useEffect(() => {
    if (registerOpen) setEditIntegrationFor(null);
  }, [registerOpen]);
  function startEditIntegration(item: IntegrationListItem): void {
    onRegisterOpenChange(false);
    setEditIntegrationFor(item);
  }

  // Set when IT approves a connection-request, so the shared dialog opens on that
  // catalog entry instead of the picker. Cleared on close, and on any other route
  // into the dialog, so a later plain "Register integration" starts from the picker.
  const [registerCatalogKey, setRegisterCatalogKey] = React.useState<string | null>(null);
  function startRegisterFromRequest(catalogKey: string): void {
    setEditIntegrationFor(null);
    setRegisterCatalogKey(catalogKey);
    onRegisterOpenChange(true);
  }
  const [deleteIntegrationFor, setDeleteIntegrationFor] =
    React.useState<IntegrationListItem | null>(null);
  const [knowledgeFor, setKnowledgeFor] = React.useState<IntegrationListItem | null>(null);
  const [availabilityBlock, setAvailabilityBlock] = React.useState<{
    integrationId: string;
    blockers: BlockingGrants;
  } | null>(null);
  const [searchParams] = useSearchParams();
  // Deep-link target: a slug (from the Grants sub-view, which carries the slug)
  // or an id (from /knowledge proposals, which carry integrationId).
  const focusParam = searchParams.get('integration');
  const matchesFocus = React.useCallback(
    (i: IntegrationListItem): boolean =>
      focusParam != null && (i.slug === focusParam || i.id === focusParam),
    [focusParam],
  );

  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const queryKey = ['integrations', cursor ?? null];
  const query = api.integrations.list.useQuery(
    queryKey,
    { query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } },
    { queryKey },
  );
  const pages = useCursorPages<IntegrationListItem>({
    query,
    cursor,
    onCursorChange: setCursor,
    getKey: integrationKey,
    // A deep link can name an integration that sorts past the loaded pages —
    // keep walking until it turns up or the registry runs out, so the target
    // never silently fails to appear.
    autoLoadWhile:
      focusParam != null ? (loaded) => !loaded.some((i) => matchesFocus(i)) : undefined,
    invalidateKey: ['integrations'],
  });
  const items = pages.items;
  const hasError = pages.hasError;
  const isLoading = pages.isLoadingFirstPage;
  const injectedEnvVarNames = items
    .map((i) => i.env_var_name)
    .filter((n): n is string => n != null);

  const queryClient = useQueryClient();
  const signInTests = useSignInTests(
    () => void queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  );
  // Kept here, not in each card: refreshing the list after a test remounts the cards.
  const [credentialTestResults, setCredentialTestResults] = React.useState<
    Record<string, Record<string, CredentialTestResultBody>>
  >({});
  const dropCredentialTestResult = (integrationId: string, slug: string): void =>
    setCredentialTestResults((prev) => {
      if (!prev[integrationId]?.[slug]) return prev;
      const { [slug]: _dropped, ...rest } = prev[integrationId];
      return { ...prev, [integrationId]: rest };
    });
  const dropIntegrationTestResults = (integrationId: string): void =>
    setCredentialTestResults((prev) => {
      if (!prev[integrationId]) return prev;
      const { [integrationId]: _dropped, ...rest } = prev;
      return rest;
    });
  const setCredentialPolicy = api.integrations.setCredentialPolicy.useMutation();
  const updateAvailability = api.integrations.updateIntegration.useMutation();

  async function handleAvailability(item: IntegrationListItem, active: boolean): Promise<void> {
    try {
      const res = await updateAvailability.mutateAsync({
        params: { id: item.id },
        body: { status: active ? 'active' : 'inactive' },
      });
      if (res.status !== 200) throw res;
      setAvailabilityBlock(null);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success(`${item.name} is now ${active ? 'active' : 'inactive'}.`);
    } catch (error) {
      const blockers = blockingGrantsFromError(error, 'integration.in_use');
      if (blockers) {
        setAvailabilityBlock({ integrationId: item.id, blockers });
        return;
      }
      toast.error(
        `Failed to ${active ? 'activate' : 'deactivate'} ${item.name}. Please try again.`,
      );
    }
  }

  async function handleSetApproval(
    integrationId: string,
    slug: string,
    mode: 'auto' | 'manual',
  ): Promise<void> {
    try {
      const res = await setCredentialPolicy.mutateAsync({
        params: { id: integrationId, slug },
        body: { approvalMode: mode },
      });
      if (res.status >= 300) throw res;
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success(
        mode === 'auto'
          ? `Grants to "${slug}" now auto-approve.`
          : `Grants to "${slug}" now require IT review.`,
      );
    } catch {
      toast.error('Failed to update the approval policy. Please try again.');
    }
  }

  const isFocused = matchesFocus;

  // Deep-link from the Grants sub-view or /knowledge: scroll the matching card
  // into view and ring-highlight it.
  const focusRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (focusParam && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focusParam, items.length]);

  const summary = presentSummary(items, { partial: pages.hasMore });

  return (
    <div className="space-y-6" data-testid="integrations-view">
      {/* Above the registry: what people are asking to have connected, before the
          list of what already is (docs/28 §/integrations). */}
      <ConnectionRequestsQueue onRegister={startRegisterFromRequest} />

      {/* Summary line */}
      {!isLoading && !hasError && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[13px] text-muted-foreground">
          <span>{summary.integrationsLabel}</span>
          <span aria-hidden="true" className="text-border">
            |
          </span>
          <span>{summary.connectedAppsLabel}</span>
          <span aria-hidden="true" className="text-border">
            |
          </span>
          <span className="inline-flex items-center gap-1 font-sans">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            {summary.note}
          </span>
        </div>
      )}

      {/* States */}
      {hasError ? (
        <Alert variant="destructive" data-testid="integrations-error">
          <AlertTitle>Couldn't load integrations</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the integration registry. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-3" data-testid="integrations-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-32 w-full rounded-gl bg-muted motion-safe:animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No integrations registered"
          description="The registration wizard creates an integration together with its first credential, so apps can declare grants against known slugs."
          action={
            <Button onClick={() => onRegisterOpenChange(true)} size="sm">
              + Register integration
            </Button>
          }
        />
      ) : (
        <div className="space-y-5" data-testid="integrations-list">
          {items.map((item) => (
            <IntegrationCard
              key={item.id}
              vm={presentIntegrationCard(item)}
              item={item}
              hasPendingGrants={pendingGrants?.some((grant) => grant.integration_id === item.id)}
              focused={isFocused(item)}
              focusRef={isFocused(item) ? focusRef : undefined}
              onAddCredential={() => setAddCredentialFor(item)}
              onEditCredential={(slug) => setEditCredentialFor({ integration: item, slug })}
              onDeleteCredential={(slug) => setDeleteCredentialFor({ integration: item, slug })}
              onSetApproval={(slug, mode) => void handleSetApproval(item.id, slug, mode)}
              onCredentialTested={(slug, result) => {
                setCredentialTestResults((prev) => ({
                  ...prev,
                  [item.id]: { ...prev[item.id], [slug]: result },
                }));
                void queryClient.invalidateQueries({ queryKey: ['integrations'] });
              }}
              credentialTestResults={credentialTestResults[item.id] ?? {}}
              signInTests={signInTests}
              onEditIntegration={() => startEditIntegration(item)}
              onDeleteIntegration={() => setDeleteIntegrationFor(item)}
              onEditKnowledge={() => setKnowledgeFor(item)}
              canChangeAvailability={userRole === 'org_admin'}
              availabilityPending={updateAvailability.isPending}
              availabilityBlockers={
                availabilityBlock?.integrationId === item.id ? availabilityBlock.blockers : null
              }
              onAvailabilityChange={(active) => void handleAvailability(item, active)}
            />
          ))}
        </div>
      )}

      {!hasError && !isLoading && (
        <LoadMore
          hasMore={pages.hasMore}
          isLoadingMore={pages.isLoadingMore}
          failed={pages.loadMoreFailed}
          onLoadMore={pages.loadMore}
          onRetry={pages.retry}
          noun="integrations"
          testId="integrations-load-more"
        />
      )}

      {/* One dialog serves both registration (header trigger) and editing (per-card
          trigger) — only one can be open at a time, so a single mount switches mode
          off whether `integration` is set (docs/28 §/integrations). */}
      <IntegrationDialog
        open={registerOpen || editIntegrationFor !== null}
        onOpenChange={(o) => {
          if (!o) {
            onRegisterOpenChange(false);
            setEditIntegrationFor(null);
            setRegisterCatalogKey(null);
          }
        }}
        integration={editIntegrationFor}
        onSecretReplaced={dropCredentialTestResult}
        onTestTargetChanged={dropIntegrationTestResults}
        initialCatalogKey={registerCatalogKey}
        injectedEnvVarNames={injectedEnvVarNames}
        requireDataAccessTerms={requireDataAccessTerms}
        {...(onDataAccessTermsAccepted ? { onDataAccessTermsAccepted } : {})}
      />
      <AddCredentialDialog
        integration={addCredentialFor}
        open={addCredentialFor !== null}
        onOpenChange={(o) => !o && setAddCredentialFor(null)}
      />
      <EditCredentialDialog
        integration={editCredentialFor?.integration ?? null}
        credentialSlug={editCredentialFor?.slug ?? null}
        open={editCredentialFor !== null}
        onOpenChange={(o) => !o && setEditCredentialFor(null)}
        onSecretReplaced={dropCredentialTestResult}
      />
      <DeleteCredentialDialog
        integration={deleteCredentialFor?.integration ?? null}
        credentialSlug={deleteCredentialFor?.slug ?? null}
        open={deleteCredentialFor !== null}
        onOpenChange={(o) => !o && setDeleteCredentialFor(null)}
      />
      <DeleteIntegrationDialog
        integration={deleteIntegrationFor}
        open={deleteIntegrationFor !== null}
        onOpenChange={(o) => !o && setDeleteIntegrationFor(null)}
      />
      <IntegrationKnowledgeSheet
        integration={knowledgeFor}
        userRole={userRole}
        open={knowledgeFor !== null}
        onOpenChange={(o) => !o && setKnowledgeFor(null)}
      />
    </div>
  );
}

const stringOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** Whether a credential test can run for this integration's auth mode (docs/09 §Testing a credential). */
function canTestCredentials(item: IntegrationListItem): boolean {
  if (item.auth_mode === 'system' || item.auth_mode === 'user_delegated') return false;
  if (item.catalog_key === 'google-workspace' && item.auth_mode === 'service_account') return false;
  return item.token_endpoint_auth_method !== 'github_app';
}

function IntegrationCard({
  vm,
  item,
  hasPendingGrants,
  focused,
  focusRef,
  onAddCredential,
  onEditCredential,
  onDeleteCredential,
  onSetApproval,
  onCredentialTested,
  credentialTestResults,
  signInTests,
  onEditIntegration,
  onDeleteIntegration,
  onEditKnowledge,
  canChangeAvailability,
  availabilityPending,
  availabilityBlockers,
  onAvailabilityChange,
}: {
  vm: IntegrationCardVM;
  // The raw projection — id/slug for the Test-connection fetch.
  item: IntegrationListItem;
  hasPendingGrants?: boolean;
  focused: boolean;
  focusRef?: React.Ref<HTMLDivElement>;
  onAddCredential: () => void;
  onEditCredential: (slug: string) => void;
  onDeleteCredential: (slug: string) => void;
  onSetApproval: (slug: string, mode: 'auto' | 'manual') => void;
  onCredentialTested: (slug: string, result: CredentialTestResultBody) => void;
  credentialTestResults: Record<string, CredentialTestResultBody> | undefined;
  signInTests: SignInTests;
  onEditIntegration: () => void;
  onDeleteIntegration: () => void;
  onEditKnowledge: () => void;
  canChangeAvailability: boolean;
  availabilityPending: boolean;
  availabilityBlockers: BlockingGrants | null;
  onAvailabilityChange: (active: boolean) => void;
}): React.JSX.Element {
  return (
    <div ref={focusRef} className={cn(focused && 'rounded-gl-lg ring-2 ring-ring')}>
      <GroupCard
        data-testid={`integration-card-${vm.slug}`}
        statusAccessory={
          <Switch
            checked={item.status !== 'inactive'}
            disabled={!canChangeAvailability || availabilityPending}
            onCheckedChange={onAvailabilityChange}
            aria-label={`${item.name} availability`}
          />
        }
        title={
          <div className="flex items-center gap-3">
            <IntegrationTile
              slug={vm.slug}
              catalogKey={vm.catalogKey}
              displayName={vm.name}
              size={40}
            />
            <span className="text-[15px] font-semibold tracking-tight text-foreground">
              {vm.name}
            </span>
            {/* Integration-level pending signal, surfaced at the card header now
                that the credential list has no section header of its own. */}
            {hasPendingGrants && <StatusBadge variant="pending">Review needed</StatusBadge>}
            {isPlainHttpUrl(item.base_url) && <NotEncryptedBadge />}
            <TlsConfigBadge config={item.tls_config} />
          </div>
        }
      >
        {/* Registration params the mode needs but does not have. Shown on the
            integration, not a credential: the credential's secret is fine, and
            the repair is Edit integration. */}
        {vm.configurationWarning && (
          <p
            className="px-4 pt-3 text-[12.5px] text-[hsl(var(--status-amber-text))]"
            data-testid={`integration-config-warning-${vm.slug}`}
          >
            {vm.configurationWarning}
          </p>
        )}
        <CredentialsTable
          credentials={vm.credentials}
          integrationId={item.id}
          onEditCredential={onEditCredential}
          onDeleteCredential={onDeleteCredential}
          onSetApproval={onSetApproval}
          onTested={canTestCredentials(item) ? onCredentialTested : undefined}
          testResults={credentialTestResults}
          {...(isPreviewFeatureEnabled('greenlight-agents') && item.base_url
            ? { agentTest: { integrationName: item.name, baseUrl: item.base_url } }
            : {})}
          signInTest={
            isPerUserOauthIntegration(item.auth_mode, item.auth_config)
              ? {
                  catalogKey: item.catalog_key,
                  authorizeUrl: stringOrNull(item.auth_config?.['authorize_url']),
                  tests: signInTests,
                }
              : undefined
          }
        />
        {availabilityBlockers && (
          <Alert variant="destructive" className="mx-4 mb-3 w-auto">
            <AlertTitle>Can't make {item.name} inactive</AlertTitle>
            <AlertDescription>
              <p>
                Revoke grants for {blockingHolderNames(availabilityBlockers).join(', ')} before
                trying again.
              </p>
              <Button variant="link" size="sm" className="h-auto px-0 text-destructive" asChild>
                <Link to={vm.grantsHref}>View blocking grants</Link>
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {/* min-h-11 gives a 44px touch target on mobile; md+ reverts to the compact sm height. */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 md:min-h-0"
            onClick={onAddCredential}
          >
            Add credential
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 md:min-h-0"
            onClick={onEditIntegration}
          >
            Edit integration
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 md:min-h-0"
            onClick={onEditKnowledge}
          >
            Edit integration knowledge
          </Button>
          {isPreviewFeatureEnabled('greenlight-agents') &&
            !isPerUserOauthIntegration(item.auth_mode, item.auth_config) && (
              <IntegrationAgentActions slug={vm.slug} name={vm.name} />
            )}
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11 text-destructive hover:text-destructive md:min-h-0"
            onClick={onDeleteIntegration}
          >
            Delete integration
          </Button>
        </div>
      </GroupCard>
    </div>
  );
}
